import {
  ArrowDown, ArrowUp, CheckCircle, CheckSquare, ChevronDown, ChevronUp, Clock, CreditCard, Eye, EyeOff, Hash,
  Home,
  Link2, Tag, Trash2, Type,
} from 'react-feather';
import { FormManager } from 'react-form-state-manager';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import intersection from 'lodash/intersection';
import keyBy from 'lodash/keyBy';
import lowerFirst from 'lodash/lowerFirst';
import map from 'lodash/map';
import upperFirst from 'lodash/upperFirst';

import { Locale, ParticipantFieldScope, ParticipantFieldType } from '__generated__/graphql';

import { ErrorBag } from '../../../../common/helpers';
import { TranslatableEditor, TranslatableInput } from '../../../Translations/Inputs';
import { Translation } from '../../../Translations/helpers';
import GetTicketsAndProductsForParticipantFieldsQuery from './GetTicketsAndProductsForParticipantFieldsQuery';
import ParticipantFieldConfigForm, { hasConfigForm } from './Configs/ParticipantFieldConfigForm';
import UI from '../../../../common/UI';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

const types = {
  [ParticipantFieldScope.order]: [
    ParticipantFieldType.short_text,
    ParticipantFieldType.integer,
    ParticipantFieldType.selection,
    ParticipantFieldType.option,
    ParticipantFieldType.address,
    ParticipantFieldType.iban,
    ParticipantFieldType.time,
  ],
  [ParticipantFieldScope.purchase]: [
    ParticipantFieldType.short_text,
    ParticipantFieldType.integer,
    ParticipantFieldType.selection,
    ParticipantFieldType.option,
    ParticipantFieldType.address,
    ParticipantFieldType.iban,
    ParticipantFieldType.time,
    ParticipantFieldType.au_license,
    ParticipantFieldType.knwu_license,
    ParticipantFieldType.kwbn_member,
    ParticipantFieldType.ntfu_member,
  ],
};

const variables = {
  [ParticipantFieldScope.order]: [
    'email',
    'firstName',
    'lastName',
  ],
  [ParticipantFieldScope.purchase]: [
    'email',
    'firstName',
    'lastName',
    'dateOfBirth',
    'gender',
    'nationality',
    'street',
    'houseNumber',
    'zipCode',
    'city',
    'extraAddressLine',
    'country',
    'phone',
    'emergencyPhone',
    'locale',
    'ticket.title',
    'ticket.rate',
  ],
};

interface Promotion {
  id: string;
}

interface Sellable {
  id: string;
  promotions: Promotion[];
}

interface ParticipantFieldChoiceData {
  id?: string;
  title: string;
  enabled: boolean;
}

interface SellableData {
  id: string;
}

interface ParticipantFieldData {
  title?: string;
  title_translations?: Translation[];
  description?: string;
  description_translations?: Translation[];
  scope?: ParticipantFieldScope;
  type?: ParticipantFieldType;
  required: boolean;
  unique: boolean;
  choices: ParticipantFieldChoiceData[];
  tickets: SellableData[];
  products: SellableData[];
  promotions: SellableData[];
  validation_url?: string;
  validation_success_response?: string;
  config: object | null;
}

interface ParticipantFieldChoice {
  id: string;
  title: string;
  deletable: boolean;
}

interface ParticipantFieldFormProps {
  form: FormManager<ParticipantFieldData>;
  field?: {
    id: string;
    choices: ParticipantFieldChoice[];
    type: ParticipantFieldType;
    scope: ParticipantFieldScope;
    validation_url?: string;
    field_entry_count: number;
  };
  event: {
    id: string;
    locale: Locale;
  };
  errors: ErrorBag;
}

export const fieldTypeIcons = {
  [ParticipantFieldType.short_text]: <Type />,
  [ParticipantFieldType.integer]: <Hash />,
  [ParticipantFieldType.selection]: <CheckSquare />,
  [ParticipantFieldType.option]: <CheckCircle />,
  [ParticipantFieldType.iban]: <CreditCard />,
  [ParticipantFieldType.address]: <Home />,
  [ParticipantFieldType.time]: <Clock />,
  [ParticipantFieldType.au_license]: <Link2 />,
  [ParticipantFieldType.knwu_license]: <Link2 />,
  [ParticipantFieldType.kwbn_member]: <Link2 />,
  [ParticipantFieldType.ntfu_member]: <Link2 />,
};

const ParticipantFieldForm = ({ form, field, event, errors }: ParticipantFieldFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const { data } = useQuery(
    GetTicketsAndProductsForParticipantFieldsQuery,
    {
      variables: {
        project_id: project.id,
        event_id: event.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const tickets = data?.project.event.tickets || [];
  const products = data?.project.event.products || [];

  const fieldType = field?.type || form.values.type;
  const fieldScope = field?.scope || form.values.scope;

  const hasChoices = [
    ParticipantFieldType.option,
    ParticipantFieldType.selection,
  ].includes(fieldType);

  const canBeUnique = [
    ParticipantFieldType.short_text,
    ParticipantFieldType.integer,
    ParticipantFieldType.au_license,
    ParticipantFieldType.knwu_license,
    ParticipantFieldType.kwbn_member,
    ParticipantFieldType.ntfu_member,
  ].includes(fieldType);

  // Generate unique key props for the list of choice forms
  const choiceKeys = useRef(form.values.choices.map((value, index) => index));

  const addChoice = () => {
    form.append('choices', {
      title_translations: [],
      enabled: true,
    });
    choiceKeys.current.push(Math.max(0, ...choiceKeys.current) + 1);
  };

  const deleteChoice = (index: number) => {
    form.delete('choices', index);
    choiceKeys.current.splice(index, 1);
  };

  const moveChoiceUp = (index: number) => {
    form.moveUp('choices', index);

    const key = choiceKeys.current[index];
    choiceKeys.current[index] = choiceKeys.current[index - 1];
    choiceKeys.current[index - 1] = key;
  };

  const moveChoiceDown = (index: number) => {
    form.moveDown('choices', index);

    const key = choiceKeys.current[index];
    choiceKeys.current[index] = choiceKeys.current[index + 1];
    choiceKeys.current[index + 1] = key;
  };

  const choiceIsDeletable = (choiceId?: string) => (
    form.values.choices.length > 1 && (
      !choiceId || !field || field.choices.filter(
        ({ id, deletable }) => id === choiceId && deletable,
      ).length > 0
    )
  );

  // When selecting a type that supports choices and there are none, append the first choice
  useEffect(() => {
    if (hasChoices && form.values.choices.length === 0) {
      addChoice();
    }
  }, [hasChoices]);

  const keyedTickets = keyBy(tickets, 'id');
  const keyedProducts = keyBy(products, 'id');

  const ticketIds = map(tickets, 'id');
  const productIds = map(products, 'id');

  // We're only interested in the tickets that belong to the current event
  const selectedTicketIds = form.values.tickets
    .filter(({ id }) => ticketIds.includes(id))
    .map(({ id }) => id);

  // We're only interested in the products that belong to the current event
  const selectedProductIds = form.values.products
    .filter(({ id }) => productIds.includes(id))
    .map(({ id }) => id);

  // Indicates whether this field depends on tickets or products
  const [dependent, setDependent] = useState(false);

  useEffect(() => {
    if (selectedTicketIds.length + selectedProductIds.length > 0) {
      setDependent(true);
    }
  }, [tickets, products]);

  const toggleDependent = () => {
    if (dependent) {
      setDependent(false);

      // We can safely discard all tickets and products, including those that belong to other
      // events, because the server will only sync tickets and products that belong to the current
      // event.
      form.set((values) => ({
        ...values,
        tickets: [],
        products: [],
        promotions: [],
      }));
    } else {
      setDependent(true);
    }
  };

  const hasSelectedPromotions = (sellable: Sellable) => {
    const sellablePromotionIds = map(sellable.promotions, 'id');
    const selectedPromotionIds = map(form.values.promotions, 'id');

    return intersection(sellablePromotionIds, selectedPromotionIds).length > 0;
  };

  const deselectPromotions = (sellable: Sellable) => {
    const sellablePromotionIds = map(sellable.promotions, 'id');

    form.set('promotions', (values: Sellable[]) => (
      values.filter(({ id }) => !sellablePromotionIds.includes(id))
    ));
  };

  const selectAllTickets = () => {
    form.set('tickets', ticketIds.map((id) => ({ id })));
  };

  const deselectAllTickets = () => {
    selectedTicketIds.forEach((id) => deselectPromotions(keyedTickets[id]));
    form.set('tickets', []);
  };

  const selectAllProducts = () => {
    form.set('products', productIds.map((id) => ({ id })));
  };

  const deselectAllProducts = () => {
    selectedProductIds.forEach((id) => deselectPromotions(keyedProducts[id]));
    form.set('products', []);
  };

  /**
   * Use the same logic as the server.
   */
  const getValidationUrlKey = (title: string) => (
    lowerFirst(
      title
        .replace(/_/g, ' ')
        .replace(/[^a-z0-9 ]/iug, '')
        .split(' ')
        .map((word) => upperFirst(word.toLowerCase()))
        .join(''),
    )
  );

  const validationInputRef = useRef<HTMLInputElement>();

  const [showingIntegration, setShowingIntegration] = useState(field && !!field.validation_url);
  const [showingVariables, setShowingVariables] = useState(false);

  const insertVariable = (variable: string, serialize = true) => {
    const url = form.values.validation_url;

    if (url) {
      const key = getValidationUrlKey(variable);
      const value = serialize ? `{${key}}` : `{${variable}}`;
      const prefix = url.includes('?') ? '&' : '?';
      const newUrl = `${url}${prefix}${key}=${value}`;

      if (!url.includes(value)) {
        form.set('validation_url', newUrl);
        validationInputRef.current.value = newUrl;
      }

      validationInputRef.current.focus();
    }
  };

  // Update the variable in the validation URL when the title changes.
  const prevTitleRef = useRef(form.values.title);

  useEffect(() => {
    const url = form.values.validation_url;

    if (url) {
      const previousKey = getValidationUrlKey(prevTitleRef.current || '');
      const previousVariable = `{${previousKey}}`;
      const newKey = getValidationUrlKey(form.values.title || '');
      const newVariable = `{${newKey}}`;

      // Update the key only when used as key={variable}, always update the {variable}.
      const newUrl = url
        .replace(`${previousKey}=${previousVariable}`, `${newKey}=${newVariable}`)
        .replace(previousVariable, newVariable);

      form.set('validation_url', newUrl);
      validationInputRef.current.value = newUrl;
    }

    prevTitleRef.current = form.values.title;
  }, [form.values.title]);

  return (
    <UI.FormGrid>
      {!field && (
        <UI.InputGroup sc={{ valid: !errors.type, touched: form.touched.type }}>
          <UI.InputLabel id="type">
            {t('type')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DropdownSelect
            ariaLabelledBy="type"
            value={form.get('type')}
            options={types[form.values.scope]}
            onChange={(value) => form.set('type', value)}
            renderOption={(type, preview) => type && (
              <>
                <UI.Icon sc={{ mr: 1 }}>
                  {fieldTypeIcons[type as ParticipantFieldType]}
                </UI.Icon>
                <UI.Strong>{t(`participant_field_types.${type}`)}</UI.Strong>
                {!preview && (
                <UI.Span sc={{ mt: 1, block: true }}>
                  {t(`participant_field_types.${type}_description`)}
                </UI.Span>
                )}
              </>
            )}
          />
          <UI.ErrorMessages attribute={t('type')} errors={errors.type} />
        </UI.InputGroup>
      )}
      {field && (
        <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
          <UI.Legend>
            <UI.Icon sc={{ mr: 1 }}>
              {fieldTypeIcons[field.type]}
            </UI.Icon>
            {' '}
            {t(`participant_field_types.${field.type}`)}
          </UI.Legend>
          <UI.Div sc={{ small: true, muted: true }}>
            {t('entry_count', { count: field.field_entry_count })}
          </UI.Div>
        </UI.FlexContainer>
      )}
      <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
        <UI.InputLabel htmlFor="FieldTitle">
          {t('field_title')}
          {' '}
          <UI.RequiredMark />
        </UI.InputLabel>
        <TranslatableInput
          form={form}
          errors={errors}
          name="title"
          id="FieldTitle"
          defaultLocale={event.locale}
          title={t('field_title')}
          maxLength={255}
          placeholder={t('field_title_placeholder')}
        />
        <UI.ErrorMessages attribute={t('field_title')} errors={errors.title} />
      </UI.InputGroup>
      <UI.InputGroup sc={{ valid: !errors.description, touched: form.touched.description }}>
        <UI.InputLabel id="ParticipantFieldDescriptionLabel">
          {t('description')}
        </UI.InputLabel>
        <TranslatableEditor
          form={form}
          errors={errors}
          name="description"
          defaultLocale={event.locale}
          title={t('description')}
          attributes={{
            'aria-labelledby': 'ParticipantFieldDescriptionLabel',
          }}
        />
        <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
      </UI.InputGroup>
      {hasChoices && (
        <>
          {form.values.choices.map((choice, index) => (
            <ParticipantFieldChoiceForm
              form={form}
              index={index}
              event={event}
              choice={choice}
              errors={errors}
              moveChoiceUp={() => moveChoiceUp(index)}
              moveChoiceDown={() => moveChoiceDown(index)}
              deleteChoice={choiceIsDeletable(form.values.choices[index].id) ? () => deleteChoice(index) : undefined}
              key={choiceKeys.current[index]}
            />
          ))}

          <UI.Div>
            <UI.Button onClick={() => addChoice()}>
              {t('add_choice')}
            </UI.Button>
          </UI.Div>
        </>
      )}

      <UI.Div>
        <UI.InputGroup>
          <UI.Checkbox {...form.checkbox('required')}>
            {t('this_field_is_required')}
          </UI.Checkbox>
        </UI.InputGroup>

        {canBeUnique && (
          <UI.InputGroup>
            <UI.Checkbox {...form.checkbox('unique')}>
              {t('this_field_is_unique')}
            </UI.Checkbox>
          </UI.InputGroup>
        )}

        <UI.InputGroup>
          <UI.Checkbox
            onChange={() => toggleDependent()}
            checked={dependent}
            disabled={tickets.length + products.length === 0}
          >
            {t('only_show_with_specific_tickets_or_products')}
          </UI.Checkbox>
        </UI.InputGroup>
      </UI.Div>

      {dependent && (
      <>
        {tickets.length > 0 && (
          <UI.InputGroup sc={{ valid: !errors.tickets, touched: form.touched.tickets }}>
            <UI.InputLabel>
              {t('tickets')}
              <UI.Span sc={{ muted: true }}> • </UI.Span>
              <UI.A
                onClick={() => selectAllTickets()}
                sc={{ disabled: selectedTicketIds.length === tickets.length }}
              >
                {t('select_all')}
              </UI.A>
              <UI.Span sc={{ muted: true }}> • </UI.Span>
              <UI.A
                onClick={() => deselectAllTickets()}
                sc={{ disabled: selectedTicketIds.length === 0 }}
              >
                {t('deselect_all')}
              </UI.A>
            </UI.InputLabel>
            {tickets.map((ticket) => (
              <Fragment key={ticket.id}>
                <UI.Checkbox
                  {...form.checklist('tickets', { id: ticket.id }, {
                    key: 'id',
                    onChange: (values: Sellable[]) => (
                      !values.map(({ id }) => id).includes(ticket.id)
                              && deselectPromotions(ticket)
                    ),
                  })}
                >
                  {ticket.title}
                </UI.Checkbox>
                {selectedTicketIds.includes(ticket.id) && ticket.promotions.length > 1 && (
                  <UI.Div sc={{ pl: 4 }}>
                    <UI.Checkbox
                      checked={!hasSelectedPromotions(ticket)}
                      disabled={!hasSelectedPromotions(ticket)}
                      onChange={() => deselectPromotions(ticket)}
                    >
                      {t('all_promotions')}
                    </UI.Checkbox>
                    {ticket.promotions.map((promotion) => (
                      <UI.Checkbox
                        {...form.checklist('promotions', { id: promotion.id }, { key: 'id' })}
                        key={promotion.id}
                      >
                        <UI.Icon sc={{ muted: true }}>
                          <Tag />
                        </UI.Icon>
                        {' '}
                        {promotion.title || t('standard')}
                      </UI.Checkbox>
                    ))}
                  </UI.Div>
                )}
              </Fragment>
            ))}
            <UI.ErrorMessages attribute={t('tickets')} errors={errors.tickets} />
          </UI.InputGroup>
        )}

        {products.length > 0 && (
          <UI.InputGroup sc={{ valid: !errors.products, touched: form.touched.products }}>
            <UI.InputLabel>
              {t('products')}
              <UI.Span sc={{ muted: true }}> • </UI.Span>
              <UI.A
                onClick={() => selectAllProducts()}
                sc={{ disabled: selectedProductIds.length === products.length }}
              >
                {t('select_all')}
              </UI.A>
              <UI.Span sc={{ muted: true }}> • </UI.Span>
              <UI.A
                onClick={() => deselectAllProducts()}
                sc={{ disabled: selectedProductIds.length === 0 }}
              >
                {t('deselect_all')}
              </UI.A>
            </UI.InputLabel>
            {products.map((product) => (
              <Fragment key={product.id}>
                <UI.Checkbox
                  {...form.checklist('products', { id: product.id }, {
                    key: 'id',
                    onChange: (values: Sellable[]) => (
                      !values.map(({ id }) => id).includes(product.id)
                              && deselectPromotions(product)
                    ),
                  })}
                >
                  {product.title}
                </UI.Checkbox>
                {selectedProductIds.includes(product.id) && product.promotions.length > 1 && (
                  <UI.Div sc={{ pl: 4 }}>
                    <UI.Checkbox
                      checked={!hasSelectedPromotions(product)}
                      disabled={!hasSelectedPromotions(product)}
                      onChange={() => deselectPromotions(product)}
                    >
                      {t('all_promotions')}
                    </UI.Checkbox>
                    {product.promotions.map((promotion) => (
                      <UI.Checkbox
                        {...form.checklist('promotions', { id: promotion.id }, { key: 'id' })}
                        key={promotion.id}
                      >
                        <UI.Icon sc={{ muted: true }}>
                          <Tag />
                        </UI.Icon>
                        {' '}
                        {promotion.title || t('standard')}
                      </UI.Checkbox>
                    ))}
                  </UI.Div>
                )}
              </Fragment>
            ))}
            <UI.ErrorMessages attribute={t('products')} errors={errors.products} />
          </UI.InputGroup>
        )}
      </>
      )}

      {fieldType && hasConfigForm(fieldType) && (
        <>
          <UI.HR sc={{ my: 0 }} />
          <ParticipantFieldConfigForm
            type={fieldType}
            config={form.values.config}
            onChange={(config) => form.set('config', config)}
          />
        </>
      )}

      <UI.HR sc={{ my: 0 }} />

      <UI.Legend>
        <UI.A onClick={() => setShowingIntegration((showing) => !showing)}>
          {t('integration')}
          {' '}
          <UI.Icon>
            {showingIntegration && <ChevronUp />}
            {!showingIntegration && <ChevronDown />}
          </UI.Icon>
        </UI.A>
      </UI.Legend>

      {showingIntegration && (
        <>
          <UI.InputGroup sc={{ valid: !errors.validation_url, touched: form.touched.validation_url }}>
            <UI.InputLabel>
              {t('validation_url')}
            </UI.InputLabel>
            <UI.InputDescription>
              {form.values.title && (
                <Trans
                  i18nKey="validation_url_description"
                  values={{
                    value: '{value}',
                    title: `{${getValidationUrlKey(form.values.title)}}`,
                  }}
                >
                  <UI.A
                    onClick={() => insertVariable('value')}
                    sc={{ code: true }}
                  />
                  <UI.A
                    onClick={() => insertVariable(form.values.title)}
                    sc={{ code: true }}
                  />
                </Trans>
              )}
              {!form.values.title && (
                <Trans
                  i18nKey="validation_url_description_without_title"
                  values={{
                    value: '{value}',
                  }}
                >
                  <UI.A
                    onClick={() => insertVariable('value')}
                    sc={{ code: true }}
                  />
                </Trans>
              )}
              {' '}
              <UI.A onClick={() => setShowingVariables((showing) => !showing)}>
                {t('see_variables')}
                {' '}
                <UI.Icon>
                  {showingVariables && <ChevronUp />}
                  {!showingVariables && <ChevronDown />}
                </UI.Icon>
              </UI.A>
              {showingVariables && (
                <UI.Info sc={{ my: 2 }}>
                  <UI.UL sc={{ mb: 0 }}>
                    {variables[fieldScope].map((variable) => (
                      <UI.LI key={variable}>
                        <UI.A
                          onClick={() => insertVariable(variable, false)}
                          sc={{ code: true }}
                        >
                          {`{${variable}}`}
                        </UI.A>
                      </UI.LI>
                    ))}
                  </UI.UL>
                </UI.Info>
              )}
            </UI.InputDescription>
            <UI.DebouncedInput
              {...form.text('validation_url')}
              placeholder="https://example.com/validate?value={value}"
              ref={validationInputRef}
            />
            <UI.ErrorMessages attribute={t('validation_url')} errors={errors.validation_url} />
          </UI.InputGroup>
          <UI.InputGroup
            sc={{
              valid: !errors.validation_success_response, touched: form.touched.validation_success_response,
            }}
          >
            <UI.InputLabel>
              {t('validation_success_response')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('validation_success_response_description')}
            </UI.InputDescription>
            <UI.DebouncedInput
              {...form.text('validation_success_response')}
              maxLength={255}
              disabled={!form.values.validation_url && !form.values.validation_success_response}
            />
            <UI.ErrorMessages
              attribute={t('validation_success_response')}
              errors={errors.validation_success_response}
            />
          </UI.InputGroup>
        </>
      )}
    </UI.FormGrid>
  );
};

interface ParticipantFieldChoiceFormProps {
  form: FormManager<ParticipantFieldData>;
  index: number;
  choice: ParticipantFieldChoiceData;
  event: {
    id: string;
    locale: Locale;
  };
  errors: ErrorBag;
  moveChoiceUp: () => void;
  moveChoiceDown: () => void;
  deleteChoice?: () => void;
}

const ParticipantFieldChoiceForm = (
  { form, index, choice, event, errors, moveChoiceUp, moveChoiceDown, deleteChoice }: ParticipantFieldChoiceFormProps,
) => {
  const { t } = useTranslation();

  const key = (k?: string) => `choices.${index}${k ? `.${k}` : ''}`;

  return (
    <UI.InputGroup sc={{ valid: !errors[key()], touched: form.getTouched(key('title')) }}>
      <UI.InputLabel htmlFor={key('title')} sc={{ muted: !form.get(key('enabled')) }}>
        {`${t('choice')} ${index + 1} `}
        <UI.RequiredMark />
      </UI.InputLabel>
      <UI.FormGrid sc={{ columns: '1fr 132px', gutter: 0.5 }}>
        <UI.Div>
          <TranslatableInput
            form={form}
            errors={errors}
            name={key('title')}
            id={key('title')}
            defaultLocale={event.locale}
            title={`${t('choice')} ${index + 1}`}
            maxLength={255}
          />
        </UI.Div>
        <UI.Div sc={{ pt: 0.75 }}>
          <UI.Button
            aria-label={t('move_up_option', { option: choice?.title })}
            sc={{ blank: true, size: 'sm', color: 'gray.400' }}
            onClick={() => moveChoiceUp()}
            disabled={index === 0}
          >
            <UI.Icon>
              <ArrowUp />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            aria-label={t('move_down_option', { option: choice?.title })}
            sc={{ blank: true, size: 'sm', color: 'gray.400' }}
            onClick={() => moveChoiceDown()}
            disabled={index === form.values.choices.length - 1}
          >
            <UI.Icon>
              <ArrowDown />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            aria-label={t(
              form.get(key('enabled')) ? 'disable_option' : 'enable_option',
              { option: choice?.title },
            )}
            sc={{
              blank: form.get(key('enabled')),
              size: 'sm',
              color: form.get(key('enabled')) ? 'gray.400' : 'gray.600',
            }}
            onClick={
              () => form.set(key('enabled'), ((enabled: boolean) => !enabled))
            }
          >
            <UI.Icon>
              {form.get(key('enabled')) ? <Eye /> : <EyeOff />}
            </UI.Icon>
          </UI.Button>
          <UI.Button
            onClick={() => deleteChoice()}
            sc={{ blank: true, color: 'gray.400', size: 'sm' }}
            disabled={!deleteChoice}
          >
            <UI.Icon>
              <Trash2 />
            </UI.Icon>
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
      <UI.ErrorMessages attribute={t('choice')} errors={errors[key()]} />
    </UI.InputGroup>
  );
};

export default ParticipantFieldForm;
